<template>
  <div>
    <!-- Selected commission slots (status 5 first, then status 2) -->
    <div class="has-background-grey-lighter p-2 mt-2">
      <div class="has-text-weight-bold mb-2">
        Kiválasztott jelentések ({{ selectedCommissions.length }} / {{ maxSelectable }})
      </div>
      <div class="columns is-multiline is-variable is-1">
        <div v-for="(slot, index) in selectionSlots"
             :key="`selectionSlot_${index}`"
             class="column is-4">
          <div class="selection-slot p-2"
               :class="[
                 slot ? 'has-background-white' : 'selection-slot-empty has-text-grey',
                 index >= maxSelectable ? 'selection-slot-overflow' : '',
               ]">
            <template v-if="slot">
              <div class="is-flex is-align-items-center">
                <b-tooltip :label="Number(slot.status) === 5 ? 'Kiválasztva, nem módosítható' : 'Kiválasztva'"
                           position="is-top">
                  <b-icon :icon="Number(slot.status) === 5 ? 'lock' : 'check-circle'"
                          class="has-text-success mr-2"/>
                </b-tooltip>
                <span class="is-flex-grow-1 has-text-weight-bold selection-slot-name"
                      :title="slot.name">
                  {{ slot.name }}
                </span>
                <b-tooltip v-if="Number(slot.status) === 2"
                           label="Törlés"
                           type="is-warning"
                           class="pointer-cursor has-text-danger ml-2"
                           position="is-left">
                  <b-icon icon="trash-alt"
                          @click.native="removeCommission(slot.id)"/>
                </b-tooltip>
              </div>
              <div class="is-size-7 has-text-grey">
                {{ slot.report_date }} | {{ slot.report_type }}
              </div>
            </template>
            <template v-else>
              <b-icon icon="plus-circle"
                      class="mr-2"/>
              <span>{{ index + 1 }}. szabad hely</span>
            </template>
          </div>
        </div>
      </div>
    </div>
    <b-table
      :data="firmList"
      class="my-4 test-me"
      default-sort="date"
      default-sort-direction="desc"
      sticky-header
      :debounce-search="1000"
      striped
      :row-class="() => 'narrower-rows'"
      narrowed
      ref="clientTable"
      :paginated="true"
      :per-page="itemPerPage"
      pagination-position="top"
      height="auto"
    >
      <div class="position-absolute">
        <b-button @click="$refs.clientTable.filters = {}"
                  class="is-inline-block"
                  type="is-primary">
          Szűrök visszaállítása
        </b-button>
        <b-select v-model="itemPerPage"
                  class="is-inline-block ml-3">
          <option
            v-for="option in [25, 50, 100]"
            :value="option"
            :key="option">
            {{ option }}
          </option>
        </b-select>
      </div>
      <b-table-column label=""
                      width="50"
                      cell-class="normal-cell"
                      v-slot="props"
                      sticky
                      header-class="has-background-success has-text-white">
        <!-- Selectable -->
        <b-field v-if="Number(props.row.status) === 1">
          <b-tooltip v-if="isSelectionFull"
                     :label="`Legfeljebb ${maxSelectable} jelentés választható ki`"
                     type="is-warning"
                     position="is-right">
            <b-icon icon="plus-circle"
                    class="has-text-grey-light"/>
          </b-tooltip>
          <b-tooltip v-else
                     label="Kiválasztás"
                     position="is-right">
            <b-icon icon="plus-circle"
                    class="pointer-cursor has-text-success"
                    @click.native="addCommission(props.row.id)"/>
          </b-tooltip>
        </b-field>
        <!-- Selected -->
        <b-field v-else-if="Number(props.row.status) === 2">
          <b-tooltip label="Törlés"
                     type="is-warning"
                     class="pointer-cursor has-text-danger"
                     position="is-right">
            <b-icon icon="trash-alt"
                    @click.native="removeCommission(props.row.id)"/>
          </b-tooltip>
        </b-field>
        <!-- Selected FIX (doesn't removable, come from person) -->
        <b-field v-else-if="Number(props.row.status) === 5">
          <b-tooltip label="Kiválasztva, nem módosítható"
                     type="is-info"
                     class="has-text-success"
                     position="is-right">
            <b-icon icon="lock"/>
          </b-tooltip>
        </b-field>
        <!-- Unselectable somewhy, see it in status_name -->
        <b-field v-else>
          <b-tooltip label="Nem választható"
                     type="is-warning"
                     position="is-right">
            <b-icon icon="ban"/>
          </b-tooltip>
        </b-field>
      </b-table-column>
      <b-table-column field="name"
                      label="Név"
                      sortable
                      sticky
                      cell-class="sticky-modifier"
                      searchable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ props.row.name }}
      </b-table-column>
      <b-table-column field="report_type"
                      label="Beszámoló típusa"
                      sortable
                      cell-class="test-table-row"
                      searchable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ props.row.report_type }}
      </b-table-column>
      <b-table-column field="main_activity"
                      label="Főtevékenység"
                      sortable
                      searchable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ props.row.main_activity }}
      </b-table-column>
      <b-table-column field="status_name"
                      label="Státusz"
                      sortable
                      searchable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        <b-tag :type="getClientStatusClass(props.row.status)"
               class="is-radiusless">
          {{ props.row.status_name }}
        </b-tag>
      </b-table-column>

      <b-table-column field="public_interest"
                      label="Közérd."
                      width="50"
                      sortable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        <b-icon v-if="props.row.public_interest"
                icon="check-square"/>
        <b-icon v-else
                pack="far"
                icon="square"/>
      </b-table-column>
      <b-table-column field="mnb"
                      label="MNB"
                      width="50"
                      sortable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        <b-icon v-if="props.row.mnb"
                icon="check-square"/>
        <b-icon v-else
                pack="far"
                icon="square"/>
      </b-table-column>
      <b-table-column field="stock"
                      label="Értékp."
                      width="50"
                      sortable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        <b-icon v-if="props.row.stock"
                icon="check-square"/>
        <b-icon v-else
                pack="far"
                icon="square"/>
      </b-table-column>
      <b-table-column field="report_date"
                      label="Év"
                      numeric
                      sortable
                      cell-class="px-5"
                      searchable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ props.row.report_date }}
      </b-table-column>
      <b-table-column field="summary"
                      label="Mérlegfőösszeg"
                      numeric
                      sortable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ formattedCurrency(props.row.summary) }}
      </b-table-column>
      <b-table-column field="income"
                      label="Árbevétel"
                      numeric
                      sortable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ formattedCurrency(props.row.income) }}
      </b-table-column>
      <b-table-column field="commision_price"
                      label="Díj"
                      numeric
                      sortable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ formattedCurrency(props.row.commission_price) }}
      </b-table-column>
      <b-table-column field="work_hour"
                      label="Óra"
                      numeric
                      sortable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ props.row.work_hour }}
      </b-table-column>
      <b-table-column field="report_interval"
                      label="Jelentés kibocsátása"
                      sortable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ props.row.report_interval }}
      </b-table-column>
      <b-table-column field="comment"
                      label="Kiadott vélemény"
                      sortable
                      searchable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ props.row.comment }}
      </b-table-column>
      <b-table-column field="pmt_tipus"
                      label="PMT típusa"
                      sortable
                      searchable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ props.row.pmt_tipus }}
      </b-table-column>
      <b-table-column field="selected_inspector_name"
                      label="Ellenőr"
                      sortable
                      searchable
                      v-slot="props"
                      header-class="has-background-success has-text-white">
        {{ props.row.selected_inspector_name }}
      </b-table-column>
    </b-table>
  </div>
</template>

<script>
import { formattedCurrency, formattedDate } from '@/utils/utils'

export default {
  name: 'FirmClientSelect',
  props: {
    firmList: {
      type: Array,
      required: true,
    },
    inspectionId: {
      type: [Number, String],
      required: true,
    },
  },
  data () {
    return {
      itemPerPage: 25,
    }
  },
  methods: {
    formattedDate,
    formattedCurrency,
    getTypeText (type) {
      let typeTxt = type
      if (typeTxt === 'FIRM') typeTxt = 'Cég'
      if (typeTxt === 'PERSON') typeTxt = 'Magánszemély'
      return typeTxt
    },
    getClientStatusClass (statusID) {
      let statusClass = 'is-secondary'
      if (Number(statusID) === 1) statusClass = 'is-success'
      if (Number(statusID) === 2) statusClass = 'is-warning'
      return statusClass
    },
    async addCommission (commissionID) {
      if (this.isSelectionFull) {
        this.$store.dispatch('showMessage', {
          type: 'warning',
          msg: `Legfeljebb ${this.maxSelectable} jelentés választható ki`,
        })
        return
      }
      const resp = await this.$store.dispatch('saveSelectionCommission', {
        inspectionID: this.inspectionId,
        commissionID,
      })
      this.$emit('commissionChanged', resp)
    },
    removeCommission (commissionID) {
      this.$store.dispatch('openModal', {
        modalName: 'ConfirmationModal',
        data: {
          text: 'Biztos törli a kiválasztást?',
          confirmFunction: async () => {
            const resp = await this.$store.dispatch('deleteSelectionCommission', {
              inspectionID: this.inspectionId,
              commissionID,
            })
            this.$emit('commissionChanged', resp)
          },
        },
      })
    },
  },
  computed: {
    maxSelectable () {
      return this.$store.state.inspectionAssign.FIRM_MAX_SELECTABLE
    },
    // status 5 (fixed, from person) first, then status 2
    selectedCommissions () {
      return [
        ...this.firmList.filter(client => Number(client.status) === 5),
        ...this.firmList.filter(client => Number(client.status) === 2),
      ]
    },
    isSelectionFull () {
      return this.selectedCommissions.length >= this.maxSelectable
    },
    // when more comes prefilled than allowed, show every selected one instead of
    // cutting the list at the limit
    selectionSlots () {
      const slotCount = Math.max(this.maxSelectable, this.selectedCommissions.length)
      const slots = []
      for (let i = 0; i < slotCount; i++) {
        slots.push(this.selectedCommissions[i] || null)
      }
      return slots
    },
  },
}
</script>

<style lang="scss"
       scoped>

div::v-deep .narrower-rows {
  td {
    white-space: nowrap;
    overflow: hidden;
    max-width: 25ch;
    text-overflow: ellipsis;
  }

  &:hover {
    td {
      text-overflow: clip;
      white-space: normal;
      word-break: break-all;
    }
  }

  td.normal-cell {
    overflow: visible;
    // every sticky td shares z-index 1, lift the action cell so its tooltip
    // isn't covered by the next (also sticky) name cell
    z-index: 2;
  }
}

::v-deep .sticky-modifier {
  left: 30px !important;
}

.selection-slot {
  height: 100%;
  border: 1px solid #dbdbdb;
  border-radius: 4px;

  .selection-slot-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.selection-slot-empty {
  border-style: dashed;
  display: flex;
  align-items: center;
}

// selections over the allowed maximum: light danger tint, has-background-white
// is a Bulma helper so the background needs !important to win
.selection-slot-overflow {
  border-color: rgba(241, 70, 104, 0.5);
  background-color: rgba(241, 70, 104, 0.07) !important;
}

</style>
